import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import { Table, Modal, Tooltip, Row, Button, Select, Avatar } from 'antd';
import _ from 'lodash';
import qs from 'qs';
import TextTruncate from 'react-text-truncate';
import ReactHtmlParser from 'react-html-parser';
import PageTitle from '../../components/PageTitle';
import TableActionBar from '../../components/TableActionBar';
import { checkPermission } from '../../utils/permission';
import { cleanObject, isValidDate, stripHtml } from '../../utils/helpers';
import {
    fetchScheduleJobs,
    fetchScheduleJob,
    removeScheduleJobs
} from '../../redux/actions/scheduleJobActions';
import ScheduleJobForm from './ScheduleJobForm';
import ScheduleJobDetail from './ScheduleJobDetail';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/schedule-jobs', breadcrumbName: 'Danh mục công việc' }
];

class ScheduleJobList extends Component {
    state = {
        isLoading: false,
        selectedRowKeys: [],
        isOpenForm: false,
        isOpenDetail: false,
        currentData: null,
        currentId: null
    };

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(previousProps) {
        if (previousProps.location.search !== this.props.location.search) this.loadData();
    }

    loadData = () => {
        const query = qs.parse(this.props.location.search.slice(1));
        this.setState({ isLoading: true });
        return this.props.fetchScheduleJobs(query)
            .catch(() => null)
            .finally(() => this.setState({ isLoading: false }));
    };

    changeFilter = (name, value) => {
        const query = cleanObject({
            ...qs.parse(this.props.location.search.slice(1)),
            [name]: value,
            page: undefined
        });
        this.props.history.push({ pathname: this.props.location.pathname, search: qs.stringify(query) });
    };

    changeTable = (pagination, sorter) => {
        const query = { ...qs.parse(this.props.location.search.slice(1)) };
        if (!_.isEmpty(pagination)) {
            query.page = pagination.current;
            query.per_page = pagination.pageSize;
        }
        if (sorter.order) {
            query.sort_field = sorter.field;
            query.sort_type = sorter.order === 'ascend' ? 'ASC' : 'DESC';
        } else {
            delete query.sort_field;
            delete query.sort_type;
        }
        this.props.history.push({ pathname: this.props.location.pathname, search: qs.stringify(query) });
    };

    openDetail = id => {
        this.setState({ currentId: id });
        this.props.fetchScheduleJob(id)
            .then(currentData => this.setState({ isOpenDetail: true, currentData }))
            .catch(() => null);
    };

    remove = ids => this.props.removeScheduleJobs(ids).then(() => {
        this.setState({ selectedRowKeys: [] });
        this.loadData();
    }).catch(() => null);

    confirmRemove = ids => Modal.confirm({
        title: 'Cảnh báo',
        content: 'Bạn chắc chắn muốn xoá những công việc đã chọn?',
        okText: 'OK',
        cancelText: 'Huỷ',
        onOk: () => this.remove(ids)
    });

    reloadDetail = () => this.props.fetchScheduleJob(this.state.currentId)
        .then(currentData => this.setState({ currentData }))
        .catch(() => null);

    render() {
        const { items, pagination, config, authUser } = this.props;
        const { isLoading, selectedRowKeys, isOpenForm, isOpenDetail, currentData } = this.state;
        const query = qs.parse(this.props.location.search.slice(1));
        const columns = [
            { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: true },
            {
                title: 'Vai trò công việc', dataIndex: 'typejob_name', key: 'typejob_name', sorter: true,
                render: (text, record) => checkPermission('schedule_jobs', 'detail') ? (
                    <Tooltip title="Xem chi tiết" placement="right">
                        <div className="color-primary item-action-btn" onClick={() => this.openDetail(record.id)}>
                            <Avatar size="small" icon={<TeamOutlined />} style={{ backgroundColor: '#0a4e8fbf' }} /> {text}
                        </div>
                    </Tooltip>
                ) : text
            },
            {
                title: 'Mô tả công việc', dataIndex: 'description', key: 'description', width: 280,
                render: text => <TextTruncate line={1} truncateText="…" text={stripHtml(text || '')} />
            },
            {
                title: 'Ghi chú', dataIndex: 'note', key: 'note', width: 220,
                render: text => (
                    <Tooltip placement="right" title={ReactHtmlParser(text || '')}>
                        <div style={{ wordBreak: 'break-word' }}>
                            <TextTruncate line={1} truncateText="…" text={stripHtml(text || '')} />
                        </div>
                    </Tooltip>
                )
            },
            { title: 'Ngày tạo', dataIndex: 'created_at', key: 'created_at', sorter: true, width: 150, render: isValidDate },
            { title: 'Người tạo', dataIndex: 'created_user', key: 'created_user', sorter: true, width: 180 }
        ];

        return (
            <div>
                <PageTitle routes={routes} title="Danh mục công việc" />
                <TableActionBar
                    isShowAddButton={checkPermission('schedule_jobs', 'create')}
                    isShowDeleteButton={checkPermission('schedule_jobs', 'remove')}
                    onAdd={() => this.setState({ isOpenForm: true, currentData: null })}
                    onDelete={() => this.confirmRemove(selectedRowKeys)}
                    disabled={!selectedRowKeys.length}
                    showFilter={false}
                    onSearch={value => this.changeFilter('keyword', value)}
                    defaultKeyword={query.keyword}
                    filters={[]}
                >
                    {authUser?.role_code === 'ADMIN' ? (
                        <Select
                            allowClear
                            className="table-button"
                            placeholder="Vai trò công việc"
                            options={config.role_trees || []}
                            onChange={value => this.changeFilter('role_id', value)}
                            style={{ width: 220 }}
                            value={query.role_id ? Number(query.role_id) : undefined}
                        />
                    ) : null}
                    <Button className="table-button" icon={<ReloadOutlined />} loading={isLoading} onClick={this.loadData}>Làm mới</Button>
                </TableActionBar>
                {selectedRowKeys.length ? <Row className="mb-2">Đang chọn {selectedRowKeys.length} bản ghi</Row> : null}
                <Table
                    rowKey="id"
                    size="small"
                    dataSource={items}
                    columns={columns}
                    loading={isLoading}
                    rowSelection={{ selectedRowKeys, onChange: keys => this.setState({ selectedRowKeys: keys }) }}
                    pagination={{
                        pageSize: pagination.perPage,
                        current: pagination.currentPage,
                        total: pagination.total,
                        showTotal: total => `Tổng ${total} bản ghi`
                    }}
                    onChange={(page, filters, sorter) => this.changeTable(page, sorter)}
                    scroll={{ x: 'max-content' }}
                />
                <ScheduleJobForm
                    visible={isOpenForm}
                    currentData={currentData}
                    reloadData={this.loadData}
                    onCancel={() => this.setState({ isOpenForm: false, currentData: null })}
                />
                <ScheduleJobDetail
                    visible={isOpenDetail}
                    currentData={currentData}
                    reloadList={this.loadData}
                    reloadData={this.reloadDetail}
                    onCancel={() => this.setState({ isOpenDetail: false, currentData: null })}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    items: state.scheduleJobReducer.items,
    pagination: state.scheduleJobReducer.pagination,
    config: state.config,
    authUser: state.auth.authUser
});

const mapDispatchToProps = dispatch => ({
    fetchScheduleJobs: filters => dispatch(fetchScheduleJobs(filters)),
    fetchScheduleJob: id => dispatch(fetchScheduleJob(id)),
    removeScheduleJobs: ids => dispatch(removeScheduleJobs(ids))
});

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleJobList);
